/*
 * ewh.js — EU legal guarantee notice (Uplifts)
 *
 * Behaviour for the harmonised notice widget (snippets/ewh-*.liquid): lightbox (native <dialog>),
 * short-info tooltip, product-page accordion row and the "jump to notice" link of the cart bar.
 * Loaded only when settings.ewh_enabled is on (see layout/theme.liquid).
 *
 * Everything is delegated on document/window: the cart drawer is re-rendered through the Section
 * Rendering API on every cart change, so element-bound listeners would be lost.
 *
 * Drawer interplay: the drawer's focus trap closes the drawer on Escape (bubbling keydown listener
 * on document). While the lightbox or the tooltip is open, a capturing listener on window handles
 * Escape first and stops it, so only the topmost layer closes. The <dialog> lives inside the drawer
 * markup, so focus inside it never counts as "outside" for the trap.
 */

(() => {
  if (window.__ewhReady) return;
  window.__ewhReady = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HIDE_DELAY = 200;

  let lastTrigger = null;
  let tip = null; // { el, trigger, pinned }
  let hideTimer = null;
  let suppressFocusOpen = false;

  /* ── Lightbox ─────────────────────────────────────────────────────────── */

  function openDialog(dialog, trigger) {
    if (!dialog || dialog.open) return;

    // The official file is only downloaded on the first open.
    const template = dialog.querySelector('template[data-ewh-template]');
    if (template) template.replaceWith(template.content.cloneNode(true));

    hideTip();
    lastTrigger = trigger || null;

    const root = document.documentElement;
    dialog.__ewhLocked = !root.classList.contains('lock');
    if (dialog.__ewhLocked) root.classList.add('lock');

    dialog.addEventListener('close', onDialogClose, { once: true });

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    const closeButton = dialog.querySelector('[data-ewh-close]');
    if (closeButton) closeButton.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      onDialogClose({ target: dialog });
    }
  }

  function onDialogClose(event) {
    const dialog = event.target;
    if (dialog.__ewhLocked) document.documentElement.classList.remove('lock');
    dialog.__ewhLocked = false;

    if (lastTrigger && lastTrigger.isConnected) lastTrigger.focus({ preventScroll: true });
    lastTrigger = null;
  }

  function openDialogElement() {
    return document.querySelector('dialog[data-ewh-dialog][open]');
  }

  /* ── Tooltip ──────────────────────────────────────────────────────────── */

  function showTip(trigger, pinned) {
    const el = document.getElementById(trigger.getAttribute('data-ewh-tip'));
    if (!el) return;

    clearTimeout(hideTimer);
    if (tip && tip.el !== el) hideTip();

    el.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    tip = { el, trigger, pinned: Boolean(pinned) || Boolean(tip && tip.pinned) };
  }

  function hideTip(returnFocus) {
    clearTimeout(hideTimer);
    if (!tip) return;

    const { el, trigger } = tip;
    const focusWasInside = inTipZone(document.activeElement);
    tip = null;
    el.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');

    // Only pull focus back when it was on the trigger/tooltip, and don't let that re-open it.
    if (returnFocus && focusWasInside && trigger.isConnected) {
      suppressFocusOpen = true;
      trigger.focus();
      suppressFocusOpen = false;
    }
  }

  function scheduleHide() {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      if (tip && !tip.pinned) hideTip();
    }, HIDE_DELAY);
  }

  function inTipZone(node) {
    return Boolean(tip && node && (tip.trigger.contains(node) || tip.el.contains(node)));
  }

  /* ── Accordion row (product page) ─────────────────────────────────────── */

  function togglePanel(button) {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;

    const willOpen = panel.hidden;
    panel.hidden = !willOpen;
    document.querySelectorAll(`[data-ewh-toggle][aria-controls="${panel.id}"]`).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(willOpen));
    });

    if (willOpen && !reducedMotion.matches && typeof panel.animate === 'function') {
      panel.animate(
        [{ opacity: 0, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'none' }],
        { duration: 200, easing: 'ease-out' }
      );
    }
  }

  /* ── Jump link (cart bar → full notice) ───────────────────────────────── */

  function jumpTo(link, event) {
    const id = (link.getAttribute('href') || '').replace(/^#/, '');
    const target = id ? document.getElementById(id) : null;
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  }

  /* ── Delegated listeners ──────────────────────────────────────────────── */

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-ewh-open]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.getAttribute('data-ewh-open')), opener);
      return;
    }

    const closer = target.closest('[data-ewh-close]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }

    // A click on the <dialog> element itself is a click on the backdrop (the inner wrapper fills it).
    if (target.matches('dialog[data-ewh-dialog]')) {
      closeDialog(target);
      return;
    }

    const toggle = target.closest('[data-ewh-toggle]');
    if (toggle) {
      togglePanel(toggle);
      return;
    }

    const jump = target.closest('[data-ewh-jump]');
    if (jump) {
      jumpTo(jump, event);
      return;
    }

    const tipTrigger = target.closest('[data-ewh-tip]');
    if (tipTrigger) {
      if (tip && tip.trigger === tipTrigger && tip.pinned) {
        hideTip();
      } else {
        showTip(tipTrigger, true);
      }
      return;
    }

    if (target.closest('[data-ewh-tip-close]')) {
      hideTip(true);
      return;
    }

    if (tip && !inTipZone(target)) hideTip();
  });

  // Desktop: hover opens, leaving trigger AND tooltip closes (with a short grace period so the
  // pointer can travel onto the tooltip — WCAG 1.4.13 hoverable).
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-ewh-tip]');
    if (trigger) {
      showTip(trigger, false);
    } else if (inTipZone(event.target)) {
      clearTimeout(hideTimer);
    }
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || !tip || tip.pinned) return;
    if (inTipZone(event.target) && !inTipZone(event.relatedTarget)) {
      if (document.activeElement === tip.trigger) return;
      scheduleHide();
    }
  });

  // Keyboard: focus opens, focus leaving trigger and tooltip closes.
  document.addEventListener('focusin', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-ewh-tip]');
    if (trigger) {
      if (!suppressFocusOpen) showTip(trigger, false);
    } else if (tip && !inTipZone(event.target)) {
      hideTip();
    }
  });

  // Escape closes only the topmost layer (lightbox, then tooltip) — never the drawer underneath.
  window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;

    const dialog = openDialogElement();
    if (dialog) {
      event.preventDefault();
      event.stopPropagation();
      closeDialog(dialog);
      return;
    }

    if (tip) {
      event.stopPropagation();
      hideTip(true);
    }
  }, true);
})();
